//  ========== ENCRYPTION ==========
export async function getSnFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const encrypted = urlParams.get('_sn');
    if (!encrypted) return null;

    try {
        const snValue = await decryptText(encrypted);
        console.log(`Set SN from encrypted URL params ${snValue}`)
        return snValue;
    } catch (e) {
        console.error("Failed to decrypt _sn:", e);
        return null;
    }
}

function hexFromBytes(bytes) {
    return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
}

function bytesFromHex(hex) {
    if (typeof hex !== 'string') throw new TypeError('Expected hex string');
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < bytes.length; i++) {
        bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
    }
    return bytes;
}

// Encrypts plainText and returns a single hex string (salt + iv + ciphertext)
export async function encryptText(plainText) {
    const encoder = new TextEncoder();
    const salt = window.crypto.getRandomValues(new Uint8Array(16));
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const password = import.meta.env.VITE_ENCRYPT_KEY;

    // Derive key
    const keyMaterial = await window.crypto.subtle.importKey(
        'raw', encoder.encode(password), {name: 'PBKDF2'}, false, ['deriveKey']
    );
    const key = await window.crypto.subtle.deriveKey(
        {
            name: 'PBKDF2',
            salt: salt,
            iterations: 100000,
            hash: 'SHA-256'
        },
        keyMaterial,
        {name: 'AES-GCM', length: 256},
        false,
        ['encrypt']
    );

    // Encrypt
    const cipherBuffer = await window.crypto.subtle.encrypt(
        {name: 'AES-GCM', iv: iv},
        key,
        encoder.encode(plainText)
    );

    // Concatenate salt + iv + ciphertext (all as hex)
    return hexFromBytes(salt) + hexFromBytes(iv) + hexFromBytes(new Uint8Array(cipherBuffer));
}

// Decrypts a hex string (salt + iv + ciphertext) and returns the original text
async function decryptText(encryptedHex) {
    const decoder = new TextDecoder();
    const password = import.meta.env.VITE_ENCRYPT_KEY;

    // Extract salt (16 bytes), iv (12 bytes), ciphertext (rest)
    const saltHex = encryptedHex.slice(0, 32);
    const ivHex = encryptedHex.slice(32, 56);
    const cipherHex = encryptedHex.slice(56);

    const salt = bytesFromHex(saltHex);
    const iv = bytesFromHex(ivHex);
    const cipherBytes = bytesFromHex(cipherHex);

    // Derive key
    const encoder = new TextEncoder();
    const keyMaterial = await window.crypto.subtle.importKey(
        'raw', encoder.encode(password), {name: 'PBKDF2'}, false, ['deriveKey']
    );
    const key = await window.crypto.subtle.deriveKey(
        {
            name: 'PBKDF2',
            salt: salt,
            iterations: 100000,
            hash: 'SHA-256'
        },
        keyMaterial,
        {name: 'AES-GCM', length: 256},
        false,
        ['decrypt']
    );

    // Decrypt
    const plainBuffer = await window.crypto.subtle.decrypt(
        {name: 'AES-GCM', iv: iv},
        key,
        cipherBytes
    );

    return decoder.decode(plainBuffer);
}

window.encryptText = encryptText;